import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  IconButton,
  Tooltip,
  Box
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

function UserManagement() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    firstName: '',
    lastName: '',
    isAdmin: false
  });
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      setError('Failed to fetch users');
    }
  };

  const handleCreateUser = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user');
      }

      const data = await response.json();
      setUsers([...users, data]);
      setDialogOpen(false);
      setNewUser({
        username: '',
        password: '',
        email: '',
        firstName: '',
        lastName: '',
        isAdmin: false
      });
      setSuccess('User created successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser({
      ...user,
      password: '' // Don't include the password when editing
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    try {
      // Create a copy of the user data without the password if it's empty
      const userData = { ...selectedUser };
      if (!userData.password) {
        delete userData.password;
      }

      const response = await fetch(`${config.apiUrl}/admin/users/${selectedUser._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }

      const updatedUser = await response.json();
      setUsers(users.map(user => user._id === updatedUser._id ? updatedUser : user));
      setDialogOpen(false);
      setSuccess('User updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleOpenDialog = () => {
    setEditMode(false);
    setNewUser({
      username: '',
      password: '',
      email: '',
      firstName: '',
      lastName: '',
      isAdmin: false
    });
    setDialogOpen(true);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user._id}>
                <TableCell>
                  {user.username}
                </TableCell>
                <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.isAdmin ? 'Admin' : 'User'}</TableCell>
                <TableCell>
                  <Tooltip title="Edit User">
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={() => handleEditUser(user)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit User' : 'Create New User'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Username"
            fullWidth
            value={editMode ? selectedUser.username : newUser.username}
            onChange={(e) => editMode 
              ? setSelectedUser({ ...selectedUser, username: e.target.value })
              : setNewUser({ ...newUser, username: e.target.value })
            }
            sx={{ mb: 2, mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={editMode ? selectedUser.password : newUser.password}
            onChange={(e) => editMode 
              ? setSelectedUser({ ...selectedUser, password: e.target.value })
              : setNewUser({ ...newUser, password: e.target.value })
            }
            sx={{ mb: 2 }}
            helperText={editMode ? "Leave blank to keep current password" : ""}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={editMode ? selectedUser.email : newUser.email}
            onChange={(e) => editMode 
              ? setSelectedUser({ ...selectedUser, email: e.target.value })
              : setNewUser({ ...newUser, email: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="First Name"
            fullWidth
            value={editMode ? selectedUser.firstName : newUser.firstName}
            onChange={(e) => editMode 
              ? setSelectedUser({ ...selectedUser, firstName: e.target.value })
              : setNewUser({ ...newUser, firstName: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Last Name"
            fullWidth
            value={editMode ? selectedUser.lastName : newUser.lastName}
            onChange={(e) => editMode 
              ? setSelectedUser({ ...selectedUser, lastName: e.target.value })
              : setNewUser({ ...newUser, lastName: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editMode ? selectedUser.isAdmin : newUser.isAdmin}
                onChange={(e) => editMode 
                  ? setSelectedUser({ ...selectedUser, isAdmin: e.target.checked })
                  : setNewUser({ ...newUser, isAdmin: e.target.checked })
                }
              />
            }
            label="Admin"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editMode ? handleUpdateUser : handleCreateUser} 
            variant="contained"
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default UserManagement; 